import * as React from "react";
import { Move, Save, RotateCcw } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { getTextureOffset, updateTextureOffset, type TextureEdgeH, type TextureEdgeV } from "../../services/api";
import { useSpaceStore } from "../../stores/spaceStore";
import { useToast } from "../ui/Toast";
import type { Surface } from "../../types";

interface TextureOffsetEditorProps {
  surface: Surface;
}

export function TextureOffsetEditor({ surface }: TextureOffsetEditorProps) {
  const { spaceId } = useSpaceStore();
  const [offsetH, setOffsetH] = React.useState(0);
  const [offsetV, setOffsetV] = React.useState(0);
  const [edgeH, setEdgeH] = React.useState<TextureEdgeH>("none");
  const [edgeV, setEdgeV] = React.useState<TextureEdgeV>("none");
  const [originalOffsetH, setOriginalOffsetH] = React.useState(0);
  const [originalOffsetV, setOriginalOffsetV] = React.useState(0);
  const [originalEdgeH, setOriginalEdgeH] = React.useState<TextureEdgeH>("none");
  const [originalEdgeV, setOriginalEdgeV] = React.useState<TextureEdgeV>("none");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  const { addToast } = useToast();

  // Load texture offset when surface changes
  React.useEffect(() => {
    loadTextureOffset();
  }, [surface.type, surface.index]);

  // Track if values have changed
  React.useEffect(() => {
    setIsDirty(
      offsetH !== originalOffsetH ||
      offsetV !== originalOffsetV ||
      edgeH !== originalEdgeH ||
      edgeV !== originalEdgeV
    );
  }, [offsetH, offsetV, edgeH, edgeV, originalOffsetH, originalOffsetV, originalEdgeH, originalEdgeV]);

  const loadTextureOffset = async () => {
    setIsLoading(true);
    try {
      const response = await getTextureOffset(surface.type, surface.index, spaceId || undefined);

      if (response.status === "SUCCESS" && response.output) {
        const h = response.output.texture_offset_h;
        const v = response.output.texture_offset_v;
        const eh = response.output.texture_edge_h || "none";
        const ev = response.output.texture_edge_v || "none";
        setOffsetH(h);
        setOffsetV(v);
        setEdgeH(eh);
        setEdgeV(ev);
        setOriginalOffsetH(h);
        setOriginalOffsetV(v);
        setOriginalEdgeH(eh);
        setOriginalEdgeV(ev);
      }
    } catch (error) {
      console.error("Failed to load texture offset:", error);
      // Default values if not found (backward compatibility)
      setOffsetH(0);
      setOffsetV(0);
      setEdgeH("none");
      setEdgeV("none");
      setOriginalOffsetH(0);
      setOriginalOffsetV(0);
      setOriginalEdgeH("none");
      setOriginalEdgeV("none");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await updateTextureOffset(
        surface.type,
        surface.index,
        offsetH,
        offsetV,
        edgeH,
        edgeV,
        spaceId || undefined
      );

      if (response.status === "SUCCESS") {
        setOriginalOffsetH(offsetH);
        setOriginalOffsetV(offsetV);
        setOriginalEdgeH(edgeH);
        setOriginalEdgeV(edgeV);
        addToast("success", `Texture settings updated for ${surface.label}`);
      } else {
        throw new Error(response.message || "Failed to update texture settings");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update texture settings";
      addToast("error", message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setOffsetH(0);
    setOffsetV(0);
    setEdgeH("none");
    setEdgeV("none");
  };

  const handleIncrement = (axis: 'h' | 'v', delta: number) => {
    if (axis === 'h') {
      setOffsetH(prev => Math.round((prev + delta) * 1000) / 1000);
    } else {
      setOffsetV(prev => Math.round((prev + delta) * 1000) / 1000);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Move className="h-4 w-4" />
            Texture Alignment
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isLoading || (offsetH === 0 && offsetV === 0 && edgeH === "none" && edgeV === "none")}
              title="Reset all settings"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isDirty}
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Edge Alignment Section */}
        <div className="space-y-3 pb-3 border-b">
          <label className="text-sm font-medium">Edge Alignment</label>
          <p className="text-xs text-muted-foreground">
            Start texture from a specific edge of the mask (works consistently across all tile sizes)
          </p>
          <div className="grid grid-cols-2 gap-4">
            {/* Horizontal Edge */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Horizontal</label>
              <select
                value={edgeH}
                onChange={(e) => setEdgeH(e.target.value as TextureEdgeH)}
                disabled={isLoading}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="none">None</option>
                <option value="left">Left Edge</option>
                <option value="right">Right Edge</option>
              </select>
            </div>
            {/* Vertical Edge */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Vertical</label>
              <select
                value={edgeV}
                onChange={(e) => setEdgeV(e.target.value as TextureEdgeV)}
                disabled={isLoading}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="none">None</option>
                <option value="top">Top Edge</option>
                <option value="bottom">Bottom Edge</option>
              </select>
            </div>
          </div>
        </div>

        {/* Fine-tune Offset Section */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Fine-tune Offset (meters)</label>
          <p className="text-xs text-muted-foreground">
            Additional offset for fine adjustments after edge alignment
          </p>

          {/* Horizontal offset */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Horizontal</label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('h', -0.1)}
                disabled={isLoading}
              >
                -0.1
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('h', -0.01)}
                disabled={isLoading}
              >
                -0.01
              </Button>
              <Input
                type="number"
                step="0.001"
                value={offsetH}
                onChange={(e) => setOffsetH(parseFloat(e.target.value) || 0)}
                className="w-24 text-center"
                disabled={isLoading}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('h', 0.01)}
                disabled={isLoading}
              >
                +0.01
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('h', 0.1)}
                disabled={isLoading}
              >
                +0.1
              </Button>
            </div>
          </div>

          {/* Vertical offset */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Vertical</label>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('v', -0.1)}
                disabled={isLoading}
              >
                -0.1
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('v', -0.01)}
                disabled={isLoading}
              >
                -0.01
              </Button>
              <Input
                type="number"
                step="0.001"
                value={offsetV}
                onChange={(e) => setOffsetV(parseFloat(e.target.value) || 0)}
                className="w-24 text-center"
                disabled={isLoading}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('v', 0.01)}
                disabled={isLoading}
              >
                +0.01
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleIncrement('v', 0.1)}
                disabled={isLoading}
              >
                +0.1
              </Button>
            </div>
          </div>
        </div>

        {isDirty && (
          <p className="text-xs text-warning">
            Unsaved changes
          </p>
        )}
      </CardContent>
    </Card>
  );
}
